import { useState, KeyboardEvent } from 'react';
import { Button, Input, Card } from '@/components/common';

interface SkillsManagerProps {
  skills: string[];
  onChange: (skills: string[]) => void;
  disabled?: boolean;
}

export function SkillsManager({ skills, onChange, disabled = false }: SkillsManagerProps) {
  const [newSkill, setNewSkill] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddSkill = () => {
    const trimmed = newSkill.trim();
    if (trimmed && !skills.includes(trimmed)) {
      onChange([...skills, trimmed]);
      setNewSkill('');
      setIsAdding(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    } else if (e.key === 'Escape') {
      setNewSkill('');
      setIsAdding(false);
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    onChange(skills.filter((skill) => skill !== skillToRemove));
  };

  const handleCancel = () => {
    setNewSkill('');
    setIsAdding(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-text-primary">Skills</h3>
        {!isAdding && (
          <Button size="sm" onClick={() => setIsAdding(true)} disabled={disabled}>
            + Add Skill
          </Button>
        )}
      </div>

      {/* Skills Display */}
      <div className="flex flex-wrap gap-2">
        {skills.length === 0 ? (
          <p className="text-text-secondary text-sm">No skills added yet</p>
        ) : (
          skills.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-soft text-primary border border-primary"
            >
              {skill}
              {!disabled && (
                <button
                  onClick={() => handleRemoveSkill(skill)}
                  className="ml-1 -mr-1 p-1 text-primary hover:text-primary-dark rounded"
                  aria-label={`Remove ${skill}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </span>
          ))
        )}
      </div>

      {/* Add Skill Form */}
      {isAdding && (
        <Card className="p-4">
          <div className="space-y-4">
            <h4 className="font-semibold text-text-primary">Add Skill</h4>
            <Input
              label="Skill Name"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g., JavaScript, React, Node.js"
              fullWidth
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleAddSkill}
                disabled={!newSkill.trim() || skills.includes(newSkill.trim())}
              >
                Add Skill
              </Button>
            </div>
          </div>
        </Card>
      )}

      <p className="text-xs text-text-secondary">Press Enter to add a skill, Escape to cancel</p>
    </div>
  );
}
